import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../../Button";
import Format from "../../../helpers/Format";
import style from "./styles.module.scss";

interface Props {
  parcela: number;
  totalParcelas: number;
  valor: number;
  data: string;
  status: "pago" | "aberto" | "atraso" | "perdido" | "cancelado";
  billet_date?: string | null;
  disabled?: boolean;
  selected?: boolean;
  loadingBillet?: boolean;
  showCheckbox?: boolean;
  onSelect?(): void;
  onPayWithPix?(): void;
  onGenerateBillet?(): void;
}

const statusTranslationKeys = {
  pago: "paid",
  aberto: "open",
  atraso: "overdue",
  perdido: "lost",
  cancelado: "canceled",
};

const statusClasses = {
  pago: style.statusPaid,
  aberto: style.statusOpen,
  atraso: style.statusOverdue,
  perdido: style.statusError,
  cancelado: style.statusError,
};

const keyI18n = "translations.consultInstallments.installmentItem";

export default function ItemParcela({
  parcela,
  totalParcelas,
  valor,
  data,
  status,
  billet_date,
  selected = false,
  disabled = false,
  loadingBillet = false,
  showCheckbox = true,
  onSelect,
  onPayWithPix,
  onGenerateBillet,
}: Props) {
  const { t } = useTranslation();
  const canGenerateBillet = ["aberto", "atraso"].includes(status)
    && !disabled
    && Boolean(onGenerateBillet);
  const canPayWithPix = ["aberto", "atraso"].includes(status)
    && !disabled
    && Boolean(onPayWithPix);

  return (
    <div className={style.installmentRow}>
      <Button
        variant="outlined"
        size="extra-small"
        iconButton
        type="button"
        className={style.billetButton}
        aria-label={t(`${keyI18n}.aria.payPix`, { number: parcela })}
        tooltip={canPayWithPix ? t(`${keyI18n}.tooltips.payPix`) : undefined}
        tooltipPlacement="bottom"
        disabled={!canPayWithPix || loadingBillet}
        onClick={onPayWithPix}
        icon={<span className="material-symbols-sharp" aria-hidden="true">qr_code_2</span>}
      />
      <Button
        variant="outlined"
        size="extra-small"
        iconButton
        type="button"
        className={style.billetButton}
        aria-label={t(`${keyI18n}.aria.generateBillet`, { number: parcela })}
        tooltip={canGenerateBillet ? t(`${keyI18n}.tooltips.generateBillet`) : undefined}
        tooltipPlacement="bottom"
        disabled={!canGenerateBillet || loadingBillet}
        onClick={onGenerateBillet}
        icon={<span className="material-symbols-sharp" aria-hidden="true">barcode</span>}
      />

      <span className={`${style.installmentNumber} pgm-label-large`}>
        {parcela}/{totalParcelas}
      </span>

      <div className={style.installmentInformation}>
        <span className="pgm-label-large">
          {t(`${keyI18n}.labels.dueDate`, {
            date: Format.fullDate(Format.date(data)),
          })}
        </span>

        <span className={`${style.tag} ${style.statusTag} ${statusClasses[status]} pgm-body-small`}>
          {status === "pago" && (
            <span className={`material-symbols-sharp ${style.tagIcon}`} aria-hidden="true">
              check
            </span>
          )}
          {status === "aberto" && (
            <span
              className={`material-symbols-sharp ${style.tagIcon} ${style.filledTagIcon}`}
              aria-hidden="true"
            >
              pending
            </span>
          )}
          {status === "atraso" && (
            <span
              className={`material-symbols-sharp ${style.tagIcon} ${style.filledTagIcon}`}
              aria-hidden="true"
            >
              error
            </span>
          )}
          {["perdido", "cancelado"].includes(status) && (
            <span
              className={`material-symbols-sharp ${style.tagIcon} ${style.filledTagIcon}`}
              aria-hidden="true"
            >
              cancel
            </span>
          )}
          {t(`${keyI18n}.status.${statusTranslationKeys[status]}`)}
        </span>

        {billet_date && (
          <span className={`${style.tag} ${style.generatedBillet} pgm-body-small`}>
            {t(`${keyI18n}.labels.generatedBillet`, {
              date: Format.fullDate(Format.date(billet_date)),
            })}
          </span>
        )}
      </div>

      <div
        className={`${style.installmentSelection} ${
          showCheckbox ? "" : style.installmentSelectionWithoutCheckbox
        }`}
      >
        <span className={`${style.installmentValue} pgm-body-medium`}>
          {Format.currency(valor)}
        </span>
        {showCheckbox && (
          <label
            className={`${style.checkboxControl} ${
              disabled || status === "pago" ? style.checkboxControlDisabled : ""
            }`}
          >
            <input
              type="checkbox"
              aria-label={t(`${keyI18n}.aria.select`, { number: parcela })}
              checked={selected}
              disabled={disabled || status === "pago"}
              onChange={onSelect}
            />
          </label>
        )}
      </div>
    </div>
  );
}
